import { ChangeEvent, FC, ReactElement } from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import { nl } from 'date-fns/locale'

import { isValid } from '@/helpers/date'
import { formatDate } from '@/helpers/DateTimeParser'
import { trans } from '@/helpers/trans'
import { DateFormat } from '@/types'

import './FormDatePicker.scss'

registerLocale('nl-NL', nl)

const convertDate = (date?: Date | string | null): Date | null | undefined => (typeof date === 'string' ? new Date(date) : date)

interface FormDatePickerProps {
    selected?: Date | string | null
    minDate?: Date | string | null
    maxDate?: Date | string | null
    onChange?: (date: Date) => void
    disabled?: boolean
    invalid?: boolean
    showTimeSelect?: boolean
    showYearDropdown?: boolean
    placeholderText?: string
    feedback?: string | null
    datePickerClassName?: string
}

// Shows a date picker on larger screens and the native date input on small screens (switched by FormDatePicker.scss)
export const FormDatePicker: FC<FormDatePickerProps> = ({
    selected = new Date(),
    minDate = null,
    maxDate = null,
    onChange,
    disabled = false,
    invalid = false,
    showTimeSelect = false,
    showYearDropdown = false,
    placeholderText,
    feedback = null,
    datePickerClassName = '',
}): ReactElement => {
    const className = `form-control ${datePickerClassName} ${invalid ? 'invalid' : ''}`
    const date = convertDate(selected)
    const min = minDate ? convertDate(minDate) : null
    const max = maxDate ? convertDate(maxDate) : null
    const dateIsValid = !! date && isValid(date)
    const inputValue = dateIsValid ? formatDate(date, DateFormat.inputDate) : selected

    const handleDatePickerChange = (value: Date | null): void => {
        if (value) {
            onChange?.(value)
        }
    }

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
        // The native input gives a 'yyyy-MM-dd' string, the date picker a Date: both are passed on as a Date
        if (event.target.value) {
            onChange?.(new Date(event.target.value))
        }
    }

    return (
        <span className="form-date-picker">
            <DatePicker
                className={className}
                dateFormat="dd-MM-yyyy"
                disabled={disabled}
                selected={dateIsValid ? date : null}
                minDate={(dateIsValid && min) || undefined}
                maxDate={(dateIsValid && max) || undefined}
                onChange={handleDatePickerChange}
                showTimeSelect={showTimeSelect}
                placeholderText={placeholderText}
                popperPlacement="top-end"
                timeIntervals={15}
                timeFormat="HH:mm"
                timeCaption={trans('common.time')}
                scrollableYearDropdown={showYearDropdown}
                showYearDropdown={showYearDropdown}
                locale="nl-NL"
            />
            <input
                className="form-control form-date-picker-mobile"
                disabled={disabled}
                value={typeof inputValue === 'string' ? inputValue : ''}
                type="date"
                onChange={handleInputChange}
            />
            {(feedback && invalid) && (
                <div className="invalid-feedback active">
                    {feedback}
                </div>
            )}
        </span>
    )
}
